import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Copy, Lock, Receipt } from "lucide-react";
import { toast } from "sonner";
import { InvoiceList } from "@/components/crm/InvoiceDialog";
import { api, errorMessage } from "@/lib/api";
import { LABELS, STAGES, StageBadge, btnGold, btnLine, days, fmtAt, inp } from "@/lib/crm";
import { money } from "@/lib/pricing";
import { DateField } from "@/components/ui/date-field";

export function Timeline({ items }) {
  return (
    <ol className="space-y-2 border-l border-amber-500/40 pl-4" data-testid="estimate-timeline">
      {(items || []).map((e, i) => (
        <li key={e.id || i} data-testid={`timeline-entry-${i}`} className="relative text-sm">
          <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-amber-500" />
          <span className="font-semibold text-slate-100">{LABELS[e.stage] || e.stage}</span>
          <span className="text-slate-400"> · {fmtAt(e.at)} · {e.by_name}</span>
          {e.reason && <span className="block text-xs text-slate-500">Reason: {e.reason}</span>}
        </li>
      ))}
    </ol>
  );
}

const MAIN = STAGES.filter((s) => s !== "paid" && s !== "declined");

export function EstimatePanel({ quoteId, onLocked }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("");
  const [reason, setReason] = useState("");
  const [at, setAt] = useState("");
  const load = useCallback(() => api.get(`/quotes/${quoteId}/crm`).then((r) => setD(r.data)).catch(() => {}), [quoteId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d) onLocked?.(d.locked); }, [d, onLocked]);
  if (!d) return null;
  const back = pick && pick !== "declined" && STAGES.indexOf(pick) < STAGES.indexOf(d.stage);
  const move = async (stage) => {
    try {
      setD((await api.post(`/quotes/${quoteId}/stage`, { stage, reason, at: at || null })).data);
      toast.success(`Moved to ${LABELS[stage]}`);
      setPick(""); setReason(""); setAt("");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const revise = async () => {
    try {
      const { data } = await api.post(`/quotes/${quoteId}/revise`);
      toast.success(`Version ${data.version} created as a Draft`);
      nav(`/quotes/${data.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const invoice = async () => {
    try { setD((await api.post(`/quotes/${quoteId}/invoices`)).data); toast.success("Invoices created"); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section className="space-y-4 rounded-2xl border border-amber-500/40 bg-amber-500/[0.03] p-5" data-testid="estimate-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <StageBadge stage={d.stage} testId="estimate-stage" />
          <span className="text-slate-400" data-testid="estimate-days-in-stage">{days(d.days_in_stage)} in stage</span>
          {d.days_sent_to_paid != null && <span className="text-slate-400" data-testid="estimate-days-sent-paid">Sent → {d.paid_done ? "Paid" : "now"}: {days(d.days_sent_to_paid)}</span>}
          <span className="text-xs text-slate-500">v{d.version} · {d.rep_name}{d.project && <> · <Link to={`/projects/${d.project.id}`} data-testid="estimate-project-link" className="underline">{d.project.name}</Link></>}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {d.next_stage && <button data-testid="estimate-next-stage-button" onClick={() => move(d.next_stage)} className={btnGold}>Mark {LABELS[d.next_stage]} <ArrowRight className="h-4 w-4" /></button>}
          <select data-testid="estimate-stage-select" value={pick} onChange={(e) => setPick(e.target.value)} className={`${inp} h-10 w-48`}>
            <option value="">Pick a stage…</option>
            {[...MAIN, "declined"].filter((s) => s !== d.stage).map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
          </select>
          {pick && <button data-testid="estimate-stage-apply" onClick={() => move(pick)} className={btnLine}>Apply</button>}
        </div>
      </div>
      {(pick && (back || d.can_manage)) && (
        <div className="flex flex-wrap gap-2">
          {(back || pick === "declined") && <input data-testid="estimate-stage-reason" placeholder={back ? "Reason for moving back (required)" : "Reason (optional)"} value={reason} onChange={(e) => setReason(e.target.value)} className={`${inp} w-80`} />}
          {d.can_manage && <DateField testId="estimate-stage-date" value={at} onChange={setAt} clearable noFuture placeholder="Back-date (optional)" className={`${inp} !w-52`} />}
        </div>
      )}
      {d.locked && (
        <div data-testid="estimate-locked-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-white px-4 py-3 text-sm text-slate-300">
          <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-amber-600" /> Approved and locked. Lines and prices can't change.</span>
          <span className="flex gap-2">
            <button data-testid="estimate-revise-button" onClick={revise} className={btnLine}><Copy className="h-3.5 w-3.5" /> Revise</button>
            {d.can_invoice && <button data-testid="estimate-create-invoices-button" onClick={invoice} className={btnGold}><Receipt className="h-4 w-4" /> Create invoices</button>}
          </span>
        </div>
      )}
      {!d.locked && d.stage !== "draft" && <button data-testid="estimate-revise-button" onClick={revise} className={btnLine}><Copy className="h-3.5 w-3.5" /> Revise</button>}
      {(d.invoices.length > 0 || d.pending_final > 0) && (
        <div>
          <InvoiceList invoices={d.invoices} onChanged={load} prefix="estimate-invoice" />
          {d.pending_final > 0 && <p data-testid="estimate-final-pending" className="text-xs text-slate-500">Final invoice of {money(d.pending_final)} will be created at Shipped.</p>}
        </div>
      )}
      <details data-testid="estimate-timeline-toggle"><summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-slate-500">Timeline</summary><div className="mt-3"><Timeline items={d.timeline} /></div></details>
    </section>
  );
}

export function ProjectPicker({ value, onPick }) {
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get(`/projects?q=${encodeURIComponent(q)}`).then((r) => setProjects(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);
  const cur = projects.find((p) => p.id === value);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-800 p-4" data-testid="quote-project-picker">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Customer / project</span>
      <input data-testid="quote-project-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className={`${inp} w-48`} />
      <select data-testid="quote-project-select" value={value || ""} onChange={(e) => onPick(projects.find((p) => p.id === e.target.value) || null)} className={`${inp} w-72`}>
        <option value="">New customer from the name below</option>
        {cur && !projects.includes(cur) && <option value={cur.id}>{cur.customer_name} · {cur.name}</option>}
        {projects.map((p) => <option key={p.id} value={p.id}>{p.customer_name} · {p.name}</option>)}
      </select>
      <Link to="/customers" data-testid="quote-project-create-link" className="text-xs font-semibold text-amber-600 underline">Create customer…</Link>
    </div>
  );
}
